import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaBook, FaEdit, FaTrash, FaPlus, FaSearch, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

interface Subject {
  id: number;
  name: string;
  is_compulsory: boolean;
}

const Subjects: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editableSubject, setEditableSubject] = useState<Subject | null>(null);
  const [newSubject, setNewSubject] = useState<{ name: string; is_compulsory: boolean }>({ name: '', is_compulsory: false });
  const [userRole, setUserRole] = useState<string>('');
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  const fetchUserData = useCallback(async () => {
    try {
      const token = getCookie('token');
      if (!token) {
        dispatch(setAuth(false));
        toast.error('No authentication token found. Please log in.');
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get('/users/user');
      setUserRole(data.user.role);
      setAuthenticated(true);
      dispatch(setAuth(true));
    } catch (e) {
      dispatch(setAuth(false));
      toast.error('Error fetching user data.');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchSubjects = useCallback(async () => {
    try {
      const token = getCookie('token');
      if (!token) {
        toast.error('No authentication token found. Please log in.');
        return;
      }
      const response = await axios.get('/exams/subjects/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(response.data);
      setFilteredSubjects(response.data);
    } catch (err: any) {
      console.error('Error fetching subjects:', err);
      toast.error(err.response?.data?.detail || 'Error fetching subjects');
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchSubjects();
  }, [fetchUserData, fetchSubjects]);

  useEffect(() => {
    const filtered = subjects.filter(subject =>
      subject.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSubjects(filtered);
  }, [searchTerm, subjects]);

  const handleCreateSubject = async () => {
    if (!['admin', 'super_admin'].includes(userRole)) {
      toast.error('You do not have permissions to create subjects.');
      return;
    }
    if (!newSubject.name.trim()) {
      toast.error('Please enter a subject name');
      return;
    }
    try {
      const token = getCookie('token');
      if (!token) {
        toast.error('No authentication token found. Please log in.');
        return;
      }
      const response = await axios.post(
        '/exams/subjects/',
        { name: newSubject.name, is_compulsory: newSubject.is_compulsory },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setSubjects([...subjects, response.data]);
      toast.success('Subject created successfully!');
      setNewSubject({ name: '', is_compulsory: false });
      setShowCreateModal(false);
    } catch (error: any) {
      console.error('Error creating subject:', error.response ? error.response.data : error.message);
      toast.error(error.response?.data?.detail || 'Error creating subject');
    }
  };

  const handleEditClick = async (subject: Subject) => {
    if (!['admin', 'super_admin'].includes(userRole)) {
      toast.error('You do not have permissions to edit subjects.');
      return;
    }
    if (isEditing === subject.id && editableSubject) {
      try {
        const token = getCookie('token');
        if (!token) {
          toast.error('No authentication token found. Please log in.');
          return;
        }
        await axios.patch(
          `/exams/subjects/${editableSubject.id}/`,
          { name: editableSubject.name, is_compulsory: editableSubject.is_compulsory },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        setSubjects(subjects.map((s) => (s.id === editableSubject.id ? editableSubject : s)));
        toast.success('Subject updated successfully!');
        setIsEditing(null);
        setEditableSubject(null);
      } catch (error: any) {
        console.error('Error updating subject:', error.response ? error.response.data : error.message);
        toast.error(error.response?.data?.detail || 'Error updating subject');
      }
    } else {
      setIsEditing(subject.id);
      setEditableSubject({ ...subject });
    }
  };

  const handleDeleteClick = async (subjectId: number) => {
    if (!['admin', 'super_admin'].includes(userRole)) {
      toast.error('You do not have permissions to delete subjects.');
      return;
    }
    try {
      const token = getCookie('token');
      if (!token) {
        toast.error('No authentication token found. Please log in.');
        return;
      }
      await axios.delete(`/exams/subjects/${subjectId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(subjects.filter((subject) => subject.id !== subjectId));
      toast.success('Subject deleted successfully!');
      setShowDeleteModal(null);
    } catch (error: any) {
      console.error('Error deleting subject:', error.response ? error.response.data : error.message);
      toast.error(error.response?.data?.detail || 'Error deleting subject');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (editableSubject) {
      setEditableSubject({
        ...editableSubject,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleNewSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setNewSubject({
      ...newSubject,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!auth || !authenticated) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">You are not authenticated</h4>
          <a href="/login" className="btn btn-primary px-4 py-2">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaBook className="me-2 text-primary" />
            Subjects Management
          </h2>
          <p className="text-muted mb-0">Manage curriculum subjects and course offerings</p>
        </div>
        {['admin', 'super_admin'].includes(userRole) && (
          <button className="btn btn-primary mt-3 mt-md-0 d-flex align-items-center" onClick={() => setShowCreateModal(true)}>
            <FaPlus className="me-2" /> Add Subject
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="card-modern p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '16px' }}>
            <div className="d-flex align-items-center">
              <div className="bg-white bg-opacity-25 p-3 rounded-circle me-3">
                <FaBook className="text-white" size={24} />
              </div>
              <div className="text-white">
                <h3 className="fw-bold mb-0">{subjects.length}</h3>
                <p className="mb-0 opacity-75">Total Subjects</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card-modern p-4" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '16px' }}>
            <div className="d-flex align-items-center">
              <div className="bg-white bg-opacity-25 p-3 rounded-circle me-3">
                <FaCheckCircle className="text-white" size={24} />
              </div>
              <div className="text-white">
                <h3 className="fw-bold mb-0">{subjects.filter(s => s.is_compulsory).length}</h3>
                <p className="mb-0 opacity-75">Compulsory</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card-modern p-4" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '16px' }}>
            <div className="d-flex align-items-center">
              <div className="bg-white bg-opacity-25 p-3 rounded-circle me-3">
                <FaTimesCircle className="text-white" size={24} />
              </div>
              <div className="text-white">
                <h3 className="fw-bold mb-0">{subjects.filter(s => !s.is_compulsory).length}</h3>
                <p className="mb-0 opacity-75">Optional</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card-modern p-4 mb-4">
        <div className="input-group shadow-sm rounded-pill overflow-hidden bg-light border" style={{ maxWidth: '400px' }}>
          <span className="input-group-text bg-transparent border-0 ps-4">
            <FaSearch className="text-muted" size={14} />
          </span>
          <input
            type="text"
            className="form-control border-0 bg-transparent shadow-none"
            placeholder="Search subjects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: '0.9rem' }}
          />
        </div>
      </div>

      {/* Subjects Grid */}
      {filteredSubjects.length === 0 ? (
        <div className="card-modern p-5 text-center">
          <FaBook size={64} className="text-muted mb-3" />
          <h5 className="text-muted mb-2">No subjects found</h5>
          <p className="text-muted small mb-0">
            {searchTerm ? 'Try adjusting your search' : 'Start by adding your first subject'}
          </p>
        </div>
      ) : (
        <div className="row g-4">
          {filteredSubjects.map((subject) => (
            <div key={subject.id} className="col-md-6 col-lg-4">
              <div className="card-modern h-100">
                {isEditing === subject.id ? (
                  <div className="card-body p-4">
                    <h6 className="fw-bold mb-3 text-primary">Edit Subject</h6>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Subject Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={editableSubject?.name || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        name="is_compulsory"
                        className="form-check-input"
                        id={`edit-compulsory-${subject.id}`}
                        checked={editableSubject?.is_compulsory || false}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label small" htmlFor={`edit-compulsory-${subject.id}`}>
                        Compulsory Subject
                      </label>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success btn-sm flex-fill"
                        onClick={() => handleEditClick(subject)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setIsEditing(null);
                          setEditableSubject(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-4" style={{ background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', borderBottom: '1px solid #e5e7eb' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <h5 className="fw-bold mb-0 text-dark">{subject.name}</h5>
                        {subject.is_compulsory ? (
                          <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 rounded-pill">
                            <FaCheckCircle className="me-1" size={12} /> Compulsory
                          </span>
                        ) : (
                          <span className="badge bg-info bg-opacity-10 text-info px-3 py-2 rounded-pill">
                            Optional
                          </span>
                        )}
                      </div>
                    </div>
                    {['admin', 'super_admin'].includes(userRole) && (
                      <div className="card-footer bg-transparent border-top-0 p-4">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm flex-fill d-flex align-items-center justify-content-center"
                            onClick={() => handleEditClick(subject)}
                          >
                            <FaEdit className="me-2" size={12} /> Edit
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm flex-fill d-flex align-items-center justify-content-center"
                            onClick={() => setShowDeleteModal(subject.id)}
                          >
                            <FaTrash className="me-2" size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Subject Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={() => setShowCreateModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <h5 className="modal-title text-white fw-bold">Add New Subject</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-3">
                  <label htmlFor="subjectName" className="form-label fw-semibold">Subject Name</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control form-control-lg bg-light border-0"
                    id="subjectName"
                    placeholder="e.g., Mathematics"
                    value={newSubject.name}
                    onChange={handleNewSubjectChange}
                  />
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="is_compulsory"
                    className="form-check-input"
                    id="isCompulsory"
                    checked={newSubject.is_compulsory}
                    onChange={handleNewSubjectChange}
                  />
                  <label className="form-check-label" htmlFor="isCompulsory">
                    Mark as compulsory subject
                  </label>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateSubject}
                  disabled={!newSubject.name.trim()}
                >
                  <FaPlus className="me-2" /> Create Subject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal !== null && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={() => setShowDeleteModal(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                <h5 className="modal-title text-white fw-bold">Confirm Deletion</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteModal(null)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="text-center mb-3">
                  <FaTrash size={48} className="text-danger opacity-50" />
                </div>
                <p className="text-center mb-0">Are you sure you want to delete this subject? This action cannot be undone.</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteClick(showDeleteModal)}
                >
                  <FaTrash className="me-2" /> Delete Subject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subjects;